/**
 * QR poster dialog for an event's public join URL.
 *
 * Two responsibilities:
 *   1. Render an on-screen preview with the event name, join code, and QR.
 *      Use this view at-event to flash to a fan or print to A4.
 *   2. Offer a PNG download of a high-res print-ready version (600x800
 *      canvas with embedded QR + event name + code).
 *
 * The QR encodes the canonical `https://<origin>/join/<CODE>` URL — same
 * payload the PWA's @capacitor/barcode-scanner regex matches against
 */

import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Download, QrCode as QrCodeIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { publicViewerUrl } from '@/services/fleet';

interface QrPosterProps {
  open: boolean;
  onClose: () => void;
  eventName: string;
  joinCode: string | null;
}

const POSTER_WIDTH = 600;
const POSTER_HEIGHT = 800;

export const QrPoster: React.FC<QrPosterProps> = ({ open, onClose, eventName, joinCode }) => {
  const url = publicViewerUrl(joinCode);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [posterDataUrl, setPosterDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !joinCode) {
      setQrDataUrl(null);
      setPosterDataUrl(null);
      return;
    }
    let cancelled = false;

    void (async () => {
      // Inline QR for the dialog preview.
      const inline = await QRCode.toDataURL(url, {
        width: 240,
        margin: 1,
        color: { dark: '#ffffff', light: '#0d0d12' },
      });
      if (cancelled) return;
      setQrDataUrl(inline);

      // Print-ready composed poster — canvas with title + code + QR + URL.
      const canvas = document.createElement('canvas');
      canvas.width = POSTER_WIDTH;
      canvas.height = POSTER_HEIGHT;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.fillStyle = '#0d0d12';
      ctx.fillRect(0, 0, POSTER_WIDTH, POSTER_HEIGHT);

      ctx.fillStyle = '#f5a623';
      ctx.font = 'bold 32px "DM Sans", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('TuviON', POSTER_WIDTH / 2, 56);

      ctx.fillStyle = '#ffffff';
      ctx.font = '600 28px "DM Sans", sans-serif';
      ctx.fillText(truncate(eventName, 36), POSTER_WIDTH / 2, 110);

      ctx.fillStyle = '#b3b3bf';
      ctx.font = '500 18px "DM Sans", sans-serif';
      ctx.fillText('Scan to join or watch live', POSTER_WIDTH / 2, 148);

      const qrPng = await QRCode.toDataURL(url, {
        width: 400,
        margin: 0,
        color: { dark: '#0d0d12', light: '#ffffff' },
      });
      if (cancelled) return;
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = reject;
        img.src = qrPng;
      });
      const qrSize = 400;
      const qrX = (POSTER_WIDTH - qrSize) / 2;
      const qrY = 180;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(qrX - 20, qrY - 20, qrSize + 40, qrSize + 40);
      ctx.drawImage(img, qrX, qrY, qrSize, qrSize);

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 56px "DM Mono", monospace';
      ctx.fillText(joinCode, POSTER_WIDTH / 2, qrY + qrSize + 80);

      ctx.fillStyle = '#b3b3bf';
      ctx.font = '500 18px "DM Mono", monospace';
      ctx.fillText(url, POSTER_WIDTH / 2, qrY + qrSize + 120);

      if (cancelled) return;
      setPosterDataUrl(canvas.toDataURL('image/png'));
    })();

    return () => {
      cancelled = true;
    };
  }, [open, joinCode, eventName, url]);

  const handleDownload = () => {
    if (!posterDataUrl || !joinCode) return;
    const a = document.createElement('a');
    a.href = posterDataUrl;
    a.download = `tuvion-${slug(eventName)}-${joinCode}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-accent">
            <QrCodeIcon className="size-5" />
            QR poster
          </DialogTitle>
        </DialogHeader>

        {!joinCode ? (
          <p className="text-sm text-fg-secondary">
            Save the event first to mint a join code.
          </p>
        ) : (
          <div className="flex flex-col items-center gap-3 py-2">
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt={`QR for ${url}`}
                width={240}
                height={240}
                className="rounded border border-border"
              />
            ) : (
              <div className="size-60 animate-pulse rounded border border-border bg-bg-input" />
            )}
            <div className="text-center font-mono text-3xl font-bold tracking-widest text-fg">
              {joinCode}
            </div>
            <div className="break-all text-center font-mono text-xs text-fg-secondary">
              {url}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="default"
            onClick={handleDownload}
            disabled={!posterDataUrl}
            className="gap-2"
          >
            <Download className="size-4" />
            Download poster (PNG)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'event';
}

export default QrPoster;
